import { useState, useEffect } from "react";
import { BarChart2, Clock, Calendar, CheckCircle, Target, AlertTriangle, FolderOpen } from "lucide-react";
import { GRADES } from '../constants';
import PageHeader from '../components/PageHeader';
import LoadingDots from '../components/LoadingDots';
import ProGate, { useProAccess } from '../components/ProGate';

export default function PerformancePage() {
  const [calls,   setCalls]   = useState([]);
  const [prices,  setPrices]  = useState({});
  const [loading, setLoading] = useState(true);
  const { unlocked, tryUnlock } = useProAccess();

  useEffect(()=>{
    fetch("/api/track")
      .then(r=>r.ok?r.json():Promise.reject())
      .then(d=>{ setCalls(d.calls||[]); return d.calls||[]; })
      .then(async calls=>{
        const tickers = [...new Set(calls.map(c=>c.ticker))];
        if (!tickers.length) return;
        const results = await Promise.all(
          tickers.map(t=>fetch(`https://financialmodelingprep.com/stable/quote?symbol=${t}&apikey=P6K8plDPUocgEnr8KoaegLEZ35TpkeCW`).then(r=>r.json()).catch(()=>null))
        );
        const map = {};
        results.forEach(d=>{ if(!d) return; const q=Array.isArray(d)?d[0]:d; if(q?.symbol&&q?.price) map[q.symbol]=q.price; });
        setPrices(map);
      })
      .catch(()=>setCalls([]))
      .finally(()=>setLoading(false));
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  const enriched = calls.map(c=>{
    const current    = prices[c.ticker];
    const isOver     = c.direction === "overvalued";
    const daysOld    = Math.floor((Date.now() - new Date(c.date)) / 86400000);
    const status     = daysOld < 30 ? "pending" : daysOld > 365 ? "closed" : "active";
    const ret        = current ? (current - c.priceAtCall) / c.priceAtCall * 100 : null;
    const winning    = ret !== null && status !== "pending" ? (isOver ? ret < 0 : ret >= 0) : null;
    const hit        = current && status !== "pending" ? Math.abs(current - c.fairValue) / c.fairValue < 0.05 : false;
    return { ...c, current, ret, winning, hit, daysOld, status };
  });

  // Only count active + closed calls in stats — pending excluded
  const evaluated  = enriched.filter(c=>c.current!=null && c.status !== "pending");
  const wins       = evaluated.filter(c=>c.winning);
  const winRate    = evaluated.length ? Math.round(wins.length/evaluated.length*100) : null;
  const avgMove    = wins.length ? (wins.reduce((s,c)=>s+Math.abs(c.ret),0)/wins.length).toFixed(1) : null;
  const hitCount   = evaluated.filter(c=>c.hit).length; // eslint-disable-line no-unused-vars
  const pendingCnt = enriched.filter(c=>c.status==="pending").length;

  const GRADE_ORDER = ["A+","A","B+","B","C","D","F"];
  const byGrade = GRADE_ORDER.map(g=>{
    const gc = evaluated.filter(c=>c.grade===g);
    const gw = gc.filter(c=>c.winning).length;
    return { g, total:gc.length, wins:gw, rate:gc.length?Math.round(gw/gc.length*100):null };
  }).filter(x=>x.total>0);

  const underCalls = evaluated.filter(c=>c.direction!=="overvalued");
  const overCalls  = evaluated.filter(c=>c.direction==="overvalued");
  const underWin   = underCalls.length ? Math.round(underCalls.filter(c=>c.winning).length/underCalls.length*100) : null;
  const overWin    = overCalls.length  ? Math.round(overCalls.filter(c=>c.winning).length/overCalls.length*100)  : null;

  const GBar = ({rate, color="#00C805"}) => (
    <div style={{height:6,background:"rgba(255,255,255,0.06)",borderRadius:3,overflow:"hidden",marginTop:6}}>
      <div style={{height:"100%",width:`${rate||0}%`,background:color,borderRadius:3,transition:"width .6s ease"}}/>
    </div>
  );

  const gradeColor = g => GRADES[g]?.color || "#e8c875";

  return (
    <div style={{fontFamily:"'Inter',sans-serif",background:"#000",minHeight:"100vh",color:"#fff"}}>
      <style>{"html,body{background:#000!important;color:#fff}*{box-sizing:border-box}a{font-family:inherit;text-decoration:none}"}</style>
      <PageHeader active="performance"/>
      <div className="hma-track-pad" style={{maxWidth:1100,margin:"0 auto",padding:"48px 40px 80px"}}>

        <div style={{marginBottom:32}}>
          <div style={{fontFamily:"'Cinzel',serif",fontSize:"clamp(32px,4.5vw,46px)",fontWeight:900,background:"linear-gradient(135deg,#ffe87a,#a07030)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",marginBottom:8}}>Performance</div>
          <div style={{fontSize:14,color:"rgba(255,255,255,0.78)",maxWidth:560,lineHeight:1.7}}>
            How accurate are our composite calls? Broken down by grade and direction — no fluff, just numbers.
          </div>
        </div>

        {/* Methodology disclosure */}
        <div style={{background:"rgba(201,168,76,0.03)",border:"1px solid rgba(201,168,76,0.12)",borderRadius:16,padding:"20px 24px",marginBottom:36}}>
          <div style={{fontSize:11,fontWeight:800,textTransform:"uppercase",letterSpacing:".15em",color:"rgba(201,168,76,0.6)",marginBottom:16}}>How We Measure Performance</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16}} className="hma-method-grid">
            {[
              { icon:<Clock size={18} strokeWidth={1.5} color="#e8c875"/>,         title:"30-Day Minimum",       body:"Calls under 30 days old are marked Pending and excluded from win rate. Valuation models need time — short-term noise isn't a fair test." },
              { icon:<Calendar size={18} strokeWidth={1.5} color="#e8c875"/>,      title:"12-Month Horizon",     body:"Our fair value targets are 12-month calls, consistent with how Wall St. analysts set price targets. Calls over 1 year are marked Closed." },
              { icon:<CheckCircle size={18} strokeWidth={1.5} color="#e8c875"/>,   title:"What Counts as a Win", body:"A win is any call where the price moved in our predicted direction (up for undervalued, down for overvalued) — measured from the call date." },
              { icon:<Target size={18} strokeWidth={1.5} color="#e8c875"/>,        title:"Hit Target",           body:"A call hits its target when the live price comes within 5% of our stated fair value at any point during the active window." },
              { icon:<BarChart2 size={18} strokeWidth={1.5} color="#e8c875"/>,     title:"Live Prices",          body:"Current prices are pulled in real-time from Financial Modeling Prep. Fair values and call prices were locked at the time of analysis." },
              { icon:<AlertTriangle size={18} strokeWidth={1.5} color="#e8c875"/>, title:"Important Limitations", body:"This is AI-generated analysis, not a professional fund. No survivorship bias — all calls are shown, wins and losses alike. Not investment advice." },
            ].map(({icon,title,body})=>(
              <div key={title} style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                <span style={{flexShrink:0,marginTop:2}}>{icon}</span>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.9)",marginBottom:4}}>{title}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.72)",lineHeight:1.65}}>{body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {loading && <LoadingDots msg="Loading performance data…"/>}

        {!loading && calls.length === 0 && (
          <div style={{background:"#0F0F0F",border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,padding:"48px",textAlign:"center"}}>
            <div style={{marginBottom:16,display:"flex",justifyContent:"center"}}><BarChart2 size={44} strokeWidth={1.5} color="rgba(201,168,76,0.5)"/></div>
            <div style={{fontFamily:"'Cinzel',serif",fontSize:20,fontWeight:700,color:"#fff",marginBottom:10}}>No data yet</div>
            <div style={{fontSize:14,color:"rgba(255,255,255,0.78)",lineHeight:1.7,maxWidth:400,margin:"0 auto 24px"}}>Save some analyses to the Track Record first — performance data builds up from there.</div>
            <a href="/" style={{display:"inline-block",padding:"12px 28px",fontSize:14,fontWeight:700,background:"linear-gradient(135deg,#0D2E5A,#1B4F8A)",color:"#fff",border:"1px solid rgba(201,168,76,0.25)",borderRadius:12}}>Run an Analysis →</a>
          </div>
        )}

        {!loading && calls.length > 0 && (
          <>
            {/* Overall stats */}
            <div className="hma-track-stats" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:40}}>
              {[
                {label:"Total Calls",     val:calls.length,                           color:"#e8c875"},
                {label:"Win Rate",        val:winRate!=null?winRate+"%":"—",           color: winRate>=60?"#00C805":winRate>=40?"#e8c875":"#FF3B30"},
                {label:"Avg Move (Wins)", val:avgMove?"+"+avgMove+"%":"—",             color:"#00C805"},
                {label:"Pending (< 30d)", val:pendingCnt,                             color:"rgba(255,255,255,0.4)"},
              ].map(s=>(
                <div key={s.label} style={{background:"#0F0F0F",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"14px 22px"}}>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".12em",color:"rgba(255,255,255,0.3)",marginBottom:4}}>{s.label}</div>
                  <div style={{fontFamily:"'Rajdhani',sans-serif",fontSize:28,fontWeight:800,color:s.color}}>{s.val}</div>
                </div>
              ))}
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:20}} className="hma-perf-grid">

              {/* By Grade */}
              <div style={{background:"#0F0F0F",border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,padding:"24px"}}>
                <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:".12em",color:"rgba(255,255,255,0.45)",marginBottom:20}}>Win Rate by Grade</div>
                {byGrade.length === 0 && <div style={{fontSize:13,color:"rgba(255,255,255,0.3)"}}>Not enough data yet</div>}
                {byGrade.map(({g, total, wins, rate})=>(
                  <div key={g} style={{marginBottom:16}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:2}}>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontFamily:"'Cinzel',serif",fontSize:16,fontWeight:900,color:gradeColor(g)}}>{g}</span>
                        <span style={{fontSize:11,color:"rgba(255,255,255,0.35)"}}>{wins}/{total} calls</span>
                      </div>
                      <span style={{fontFamily:"'Rajdhani',sans-serif",fontSize:16,fontWeight:800,color:rate>=60?"#00C805":rate>=40?"#e8c875":"#FF3B30"}}>{rate!=null?rate+"%":"—"}</span>
                    </div>
                    <GBar rate={rate} color={rate>=60?"#00C805":rate>=40?"#e8c875":"#FF3B30"}/>
                  </div>
                ))}
              </div>

              {/* By Direction */}
              <div style={{background:"#0F0F0F",border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,padding:"24px"}}>
                <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:".12em",color:"rgba(255,255,255,0.45)",marginBottom:20}}>Win Rate by Direction</div>
                {[
                  {label:"Undervalued Calls", sublabel:"We called it cheap — did it rise?", count:underCalls.length, wins:underCalls.filter(c=>c.winning).length, rate:underWin, color:"#00C805"},
                  {label:"Overvalued Calls",  sublabel:"We called it expensive — did it fall?", count:overCalls.length, wins:overCalls.filter(c=>c.winning).length, rate:overWin,  color:"#FF3B30"},
                ].map(({label,sublabel,count,wins,rate,color})=>(
                  <div key={label} style={{marginBottom:24}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:"#fff",marginBottom:2}}>{label}</div>
                        <div style={{fontSize:11,color:"rgba(255,255,255,0.35)"}}>{sublabel}</div>
                        <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginTop:2}}>{wins}/{count} correct</div>
                      </div>
                      <span style={{fontFamily:"'Rajdhani',sans-serif",fontSize:28,fontWeight:800,color:rate>=60?"#00C805":rate>=40?"#e8c875":"#FF3B30",flexShrink:0,marginLeft:12}}>{rate!=null?rate+"%":"—"}</span>
                    </div>
                    <GBar rate={rate} color={rate>=60?"#00C805":rate>=40?"#e8c875":color}/>
                  </div>
                ))}

                <div style={{borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:16,marginTop:4}}>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.25)",lineHeight:1.7}}>
                    "Winning" = price moving in the direction our model predicted. A stock we called undervalued is winning if it's trading higher than our call price.
                  </div>
                </div>
              </div>
            </div>

            {/* Calls table */}
            {/* Individual call cards */}
            <div style={{marginBottom:8}}>
              <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:".12em",color:"rgba(255,255,255,0.45)",marginBottom:14}}>Our Calls</div>
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {enriched.sort((a,b)=>new Date(b.date)-new Date(a.date)).slice(0, unlocked ? enriched.length : 2).map((c,i)=>{
                  const g = GRADES[c.grade]||GRADES["C"];
                  const isOver = c.direction==="overvalued";
                  return (
                    <a key={i} href={`/?t=${c.ticker}`} className="hma-track-card" style={{display:"block",background:"#0F0F0F",border:`1px solid ${c.hit?"rgba(0,200,5,0.18)":"rgba(255,255,255,0.07)"}`,borderLeft:`3px solid ${c.status==="pending"?"rgba(255,255,255,0.15)":c.winning?"#00C805":"#FF3B30"}`,borderRadius:14,padding:"18px 22px",textDecoration:"none",color:"inherit",cursor:"pointer"}}>
                      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:16,flexWrap:"wrap",marginBottom:c.companyName?10:0}}>
                        <div>
                          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                            <span style={{fontFamily:"'Rajdhani',sans-serif",fontSize:22,fontWeight:900,color:"#fff"}}>{c.ticker}</span>
                            <span style={{fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:20,background:g.bg,color:g.color}}>{c.grade}</span>
                            <span style={{fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:20,background:isOver?"rgba(255,59,48,0.1)":"rgba(0,200,5,0.1)",color:isOver?"#FF3B30":"#00C805"}}>{isOver?"▼ Overvalued":"▲ Undervalued"}</span>
                            {c.status==="pending" && <span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:20,background:"rgba(255,255,255,0.07)",color:"rgba(255,255,255,0.4)"}}><Clock size={9} strokeWidth={2}/> Pending — {30-c.daysOld}d left</span>}
                            {c.status==="closed"  && <span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:20,background:"rgba(201,168,76,0.08)",color:"#e8c875"}}><FolderOpen size={9} strokeWidth={2}/> Closed</span>}
                            {c.status!=="pending" && c.hit && <span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:20,background:"rgba(0,200,5,0.1)",color:"#00C805"}}><Target size={9} strokeWidth={2}/> Hit Target</span>}
                            {c.status!=="pending" && !c.hit && c.winning && <span style={{fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:20,background:"rgba(0,200,5,0.06)",color:"#00C805"}}>✓ On Track</span>}
                            {c.status!=="pending" && !c.hit && c.winning===false && <span style={{fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:20,background:"rgba(255,59,48,0.06)",color:"#FF3B30"}}>✗ Off Track</span>}
                          </div>
                          <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginTop:3}}>
                            {new Date(c.date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})} · Called at ${c.priceAtCall?.toFixed(2)||"—"} · {c.daysOld}d ago
                          </div>
                        </div>
                        <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".1em",color:"rgba(255,255,255,0.3)",marginBottom:2}}>Our Fair Value</div>
                            <div style={{fontFamily:"'Rajdhani',sans-serif",fontSize:20,fontWeight:800,color:"#e8c875"}}>${c.fairValue?.toFixed(2)||"—"}</div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".1em",color:"rgba(255,255,255,0.3)",marginBottom:2}}>Price Today</div>
                            <div style={{fontFamily:"'Rajdhani',sans-serif",fontSize:20,fontWeight:800,color:"#fff"}}>{c.current?`$${c.current.toFixed(2)}`:<span className="hma-price-skel"/>}</div>
                          </div>
                          {c.ret!=null && (
                            <div style={{textAlign:"right"}}>
                              <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:".1em",color:"rgba(255,255,255,0.3)",marginBottom:2}}>Move Since Call</div>
                              <div style={{fontFamily:"'Rajdhani',sans-serif",fontSize:20,fontWeight:800,color:c.ret>2?"#00C805":c.ret<-2?"#FF3B30":"#e8c875"}}>{c.ret>=0?"+":""}{c.ret.toFixed(1)}%</div>
                            </div>
                          )}
                        </div>
                      </div>
                      {c.companyName && <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",lineHeight:1.6,borderTop:"1px solid rgba(255,255,255,0.05)",paddingTop:10}}>{c.companyName}</div>}
                    </a>
                  );
                })}
              </div>
              {!unlocked && enriched.length > 2 && (
                <>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.3)",textAlign:"center",margin:"16px 0 8px"}}>
                    + {enriched.length - 2} more call{enriched.length-2!==1?"s":""} locked
                  </div>
                  <ProGate tryUnlock={tryUnlock} label="full call history"/>
                </>
              )}
            </div>

            <div style={{marginTop:24,fontSize:12,color:"rgba(255,255,255,0.22)",lineHeight:1.7,textAlign:"center"}}>
              Win rate excludes calls under 30 days old — valuation models need time to play out. Horizon: 12 months.<br/>
              "Pending" = &lt; 30 days · "Active" = 30–365 days · "Closed" = &gt; 1 year · "Hit Target" = within 5% of fair value.<br/>
              Past performance does not guarantee future results. Not investment advice.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
